import { createRoomConfiguratorApi } from './roomConfigurator.js';
// API Client Types and Configuration
import axios from 'axios';
import { isGrmsRole } from '../types/grmsRoles.js';
import { isValidSceneIconForAuthoring } from '../types/sceneIcons.js';
function assertSceneIconsForAuthoring(scenes, field = 'scenes') {
    scenes?.forEach((scene, index) => {
        if (!isValidSceneIconForAuthoring(scene.icon)) {
            throw new Error(`${field}[${index}].icon must be empty or one of the published scene icon values`);
        }
    });
}
// Default configuration from environment
export const defaultApiConfig = {
    baseUrl: import.meta.env?.VITE_BMS_API_URL || '', // Empty string = same origin (proxied by Traefik)
    mode: import.meta.env?.VITE_BMS_MODE || 'edge',
    getAccessToken: () => null,
    getSiteCode: () => null,
    timeout: 30000,
};
// =============================================================================
// API Client Class
// =============================================================================
/**
 * Marks a request the 401 interceptor has already refreshed and replayed. A
 * symbol rather than a header, so it never reaches the wire.
 */
const RETRIED = Symbol('bmsRetriedAfterRefresh');
export class BmsApiClient {
    client;
    /**
     * `/dev/virtual/*` is deliberately mounted outside `/api/`
     * (docs/virtual-devices.md), so it needs its own base URL
     * rather than `client`'s `/api`-prefixed one. Same auth/refresh behavior,
     * via `attachAuthInterceptors`.
     */
    devClient;
    config;
    refreshPromise;
    onSession;
    onAuthFailure;
    constructor(config = {}) {
        this.config = { ...defaultApiConfig, ...config };
        this.client = axios.create({
            baseURL: `${this.config.baseUrl}/api`,
            timeout: this.config.timeout,
            withCredentials: true, // Send cookies for auth
            headers: {
                'Content-Type': 'application/json',
            },
            ...(this.config.fetch ? { adapter: 'fetch', env: { fetch: this.config.fetch } } : {}),
        });
        this.devClient = axios.create({
            baseURL: `${this.config.baseUrl}/dev/virtual`,
            timeout: this.config.timeout,
            withCredentials: true,
            headers: {
                'Content-Type': 'application/json',
            },
            ...(this.config.fetch ? { adapter: 'fetch', env: { fetch: this.config.fetch } } : {}),
        });
        this.attachAuthInterceptors(this.client);
        this.attachAuthInterceptors(this.devClient);
    }
    /** Attach access-token and 401-refresh handling to one axios instance. */
    attachAuthInterceptors(instance) {
        // Add request interceptor to attach access token
        instance.interceptors.request.use((config) => {
            const token = this.config.getAccessToken?.();
            if (token && config.headers) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        });
        // Add response interceptor for error handling
        instance.interceptors.response.use((response) => {
            const method = response.config.method?.toLowerCase();
            const url = response.config.url ?? '';
            if (typeof window !== 'undefined'
                && method != null
                && !['get', 'head', 'options'].includes(method)
                && url.includes('/sites/')) {
                window.dispatchEvent(new Event('bms:edge-access-state-changed'));
            }
            return response;
        }, async (error) => {
            // Don't try to refresh or redirect if we're already on login page
            const isLoginPage = typeof window !== 'undefined' && window.location.pathname.includes('/login');
            // Only attempt refresh if we had a token (i.e. user was logged in)
            const hadToken = !!this.config.getAccessToken?.();
            const isRefreshRequest = error.config?.url?.endsWith('/auth/refresh') ?? false;
            // BMS-spec docs/edge/rest-api.md §Native mobile REST session policy:
            // "retry the original REST request **once**" and "a second 401 is
            // terminal: clear local auth/site state and return to login". Without
            // a marker on the retry, a route that answers 401 whatever the token
            // says would refresh-and-retry forever.
            const isRetry = error.config?.[RETRIED] === true;
            if (error.response?.status === 401 && !isLoginPage && hadToken && !isRefreshRequest) {
                if (isRetry) {
                    this.handleAuthFailure();
                    throw error;
                }
                try {
                    // Browsers commonly issue several requests at once. Each 401 waits
                    // for the same refresh rather than treating the later responses as
                    // independent authentication failures.
                    await this.getRefreshPromise();
                    // Retry the original request exactly once.
                    const retryConfig = { ...error.config, [RETRIED]: true };
                    return instance.request(retryConfig);
                }
                catch {
                    // The shared refresh promise already cleared auth state.
                }
            }
            throw error;
        });
    }
    /** Update access token and broker credential together on every session exchange. */
    setSessionHandler(handler) {
        this.onSession = handler;
    }
    /** Register a callback to clear auth state on auth failure (e.g. store.logout) */
    setAuthFailureHandler(handler) {
        this.onAuthFailure = handler;
    }
    handleAuthFailure() {
        this.onAuthFailure?.();
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
            window.location.href = '/login';
        }
    }
    /** Get site-scoped path prefix */
    getSitePath() {
        const siteCode = this.config.getSiteCode?.();
        if (!siteCode) {
            throw new Error('No site selected. Please select a site first.');
        }
        return `/sites/${siteCode}`;
    }
    /** Set the site code getter */
    setSiteCodeGetter(getter) {
        this.config.getSiteCode = getter;
    }
    /** Set the access token getter */
    setAccessTokenGetter(getter) {
        this.config.getAccessToken = getter;
    }
    // ===========================================================================
    // Authentication
    // ===========================================================================
    async login(username, password) {
        const { data } = await this.client.post('/auth/login', { username, password });
        this.onSession?.(data);
        return data;
    }
    async selectSite(siteCode) {
        const { data } = await this.client.post('/auth/select-site', { siteCode });
        this.onSession?.(data);
        return data;
    }
    async logout() {
        await this.client.post('/auth/logout');
    }
    async refreshToken() {
        const { data } = await this.client.post('/auth/refresh');
        this.onSession?.(data);
        return data;
    }
    /**
     * Refresh the session through the same single in-flight refresh the 401 interceptor uses, and return the new
     * access token. For callers that cannot go through axios (a streaming `fetch`): they retry once with it.
     */
    async refreshSession() {
        return (await this.getRefreshPromise()).token;
    }
    /** Return the one in-flight refresh and clear auth state once if it fails. */
    getRefreshPromise() {
        if (!this.refreshPromise) {
            this.refreshPromise = this.refreshToken()
                .catch((error) => {
                this.handleAuthFailure();
                throw error;
            })
                .finally(() => {
                this.refreshPromise = undefined;
            });
        }
        return this.refreshPromise;
    }
    /**
     * The Current-user permission projection.
     *
     * BMS-spec `docs/edge/rest-api.md` §"Current-user permission projection".
     * `siteCode` is explicit and is never inherited from the token: omitting it
     * asks for global scope only, and passing one asks for that site's grants
     * unioned with the global ones. The site is a query parameter rather than a
     * path segment because this read is not site-scoped authority — it is the
     * question "what may I do there", which a session may legitimately ask about
     * a site it has not selected.
     *
     * This is not a site-selection exchange and does not extend the access
     * token's lifetime or change its scope.
     */
    async getCurrentUser(siteCode) {
        const { data } = await this.client.get('/auth/me', {
            params: siteCode ? { siteCode } : undefined,
        });
        return data;
    }
    // ===========================================================================
    // Users & Roles (RBAC)
    // ===========================================================================
    async getUsers(siteCode) {
        const { data } = await this.client.get('/users', {
            params: siteCode ? { site: siteCode } : undefined,
        });
        return data.users;
    }
    async getUser(userId, siteCode) {
        const { data } = await this.client.get(`/users/${userId}`, {
            params: siteCode ? { site: siteCode } : undefined,
        });
        return data;
    }
    async createUser(payload) {
        const { data } = await this.client.post('/users', payload);
        return data;
    }
    async updateUser(userId, payload) {
        const { data } = await this.client.put(`/users/${userId}`, payload);
        return data;
    }
    async deleteUser(userId) {
        await this.client.delete(`/users/${userId}`);
    }
    async updateUserRoles(userId, payload) {
        await this.client.put(`/users/${userId}/roles`, payload);
    }
    async getUserDevices(userId) {
        const { data } = await this.client.get(`/users/${userId}/devices`);
        return data.devices;
    }
    async revokeUserDevice(userId, credentialId) {
        await this.client.delete(`/users/${userId}/devices/${credentialId}`);
    }
    async createDeviceInvitation(userId, payload = {}) {
        const { data } = await this.client.post(`/users/${userId}/device-invitations`, payload);
        return data;
    }
    async getDeviceInvitations(userId) {
        const { data } = await this.client.get(`/users/${userId}/device-invitations`);
        return data.invitations;
    }
    async cancelDeviceInvitation(userId, invitationId) {
        const { data } = await this.client.delete(`/users/${userId}/device-invitations/${invitationId}`);
        return data;
    }
    async confirmDeviceInvitation(userId, invitationId, payload) {
        const { data } = await this.client.post(`/users/${userId}/device-invitations/${invitationId}/confirm`, payload);
        return data;
    }
    /**
     * This intentionally accepts only a claim receipt. It is not an authenticated
     * administrator operation and callers must keep the receipt in ephemeral UI
     * state until it reaches a terminal state.
     */
    async getDeviceInvitationClaimStatus(invitationId, claimReceipt) {
        const { data } = await this.client.post(`/auth/device-invitations/${invitationId}/status`, {
            claimReceipt,
        });
        return data;
    }
    async getRoles() {
        const { data } = await this.client.get('/roles');
        return data.roles;
    }
    // ===========================================================================
    // Site Management
    // ===========================================================================
    async getSites() {
        const { data } = await this.client.get('/sites');
        return data.sites;
    }
    async createSite(site) {
        const { data } = await this.client.post('/sites', site);
        return data;
    }
    async getSiteConfig() {
        const { data } = await this.client.get('/site');
        return data;
    }
    /** BMS-spec rest-api.md POST /api/site/hvac/changeover/apply: publication accepted, not actuation. */
    async applyHvacChangeover(mode) {
        const { data } = await this.client.post('/site/hvac/changeover/apply', { mode });
        return data;
    }
    async updateSiteConfig(config) {
        const { data } = await this.client.patch('/site', config);
        return data;
    }
    /**
     * Change the occupancy-automation setpoints and scene codes, and nothing else.
     *
     * The narrow counterpart of `updateSiteConfig`, under its own permission
     * `site:write_occupancy_automation`. Three things about it differ from every
     * other site writer and each is load-bearing for a caller:
     *
     *   - **The body is merged field by field.** An absent field is left alone; a
     *     scene code sent as `null` removes that action. `enabled` — and every
     *     other key — is `400 invalid_request`, not a dropped key: whether the
     *     mechanism runs at all is site configuration and stays with `site:write`.
     *   - **The answer is the resulting `occupancy_automation` object alone**,
     *     never the site config. A caller holding this permission has no business
     *     reading back settings it cannot see, so the site-config cache is
     *     invalidated rather than written from the response.
     *   - **The site comes from the session token**, exactly as `PATCH /api/site`
     *     does, so this is only callable with a site selected. With nothing stored
     *     to edit the route answers `409 resource_state_conflict`.
     *
     * BMS-spec `docs/edge/rest-api.md` §PATCH `/api/site/occupancy-automation`,
     * `docs/grms/specification.md` §8.4.1 and §14.2.
     */
    async updateOccupancyAutomation(patch) {
        const { data } = await this.client.patch('/site/occupancy-automation', patch);
        return data;
    }
    /**
     * The house checkout time, `HH:MM` in the site's zone
     * (`site:write_occupancy_automation`; BMS-spec GRMS §14.2).
     */
    async updateCheckoutTime(checkoutTime) {
        const { data } = await this.client.patch('/site/checkout-time', { checkout_time: checkoutTime });
        return data;
    }
    // ===========================================================================
    // Buildings & Floors (Site-scoped, full profile only)
    // ===========================================================================
    async getBuildings() {
        const { data } = await this.client.get(`${this.getSitePath()}/buildings`);
        return data.buildings ?? data;
    }
    async createBuilding(building) {
        const { data } = await this.client.post(`${this.getSitePath()}/buildings`, building);
        return data;
    }
    async updateBuilding(buildingCode, building) {
        const { data } = await this.client.put(`${this.getSitePath()}/buildings/${buildingCode}`, building);
        return data;
    }
    async deleteBuilding(buildingCode) {
        await this.client.delete(`${this.getSitePath()}/buildings/${buildingCode}`);
    }
    async getFloors(buildingCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/buildings/${buildingCode}/floors`);
        return data.floors ?? data;
    }
    async createFloor(buildingCode, floor) {
        const { data } = await this.client.post(`${this.getSitePath()}/buildings/${buildingCode}/floors`, floor);
        return data;
    }
    async updateFloor(buildingCode, floorCode, floor) {
        const { data } = await this.client.put(`${this.getSitePath()}/buildings/${buildingCode}/floors/${floorCode}`, floor);
        return data;
    }
    async deleteFloor(buildingCode, floorCode) {
        await this.client.delete(`${this.getSitePath()}/buildings/${buildingCode}/floors/${floorCode}`);
    }
    // ===========================================================================
    // Provisioning (Unclaimed Gateways)
    // ===========================================================================
    async getDiscoveredGateways() {
        const { data } = await this.client.get('/provisioning/gateways');
        return data.gateways ?? data;
    }
    async getDiscoveredGateway(hwUid) {
        const { data } = await this.client.get(`/provisioning/gateways/${hwUid}`);
        return data;
    }
    async claimGateway(hwUid, params) {
        const { data } = await this.client.post(`/provisioning/gateways/${hwUid}/claim`, params);
        return data;
    }
    async getOrphanedGateways() {
        const { data } = await this.client.get('/provisioning/orphaned');
        return data.orphans ?? [];
    }
    // No orphan unclaim method: BMS-spec docs/edge/rest-api.md §Orphan recovery controls
    // removes `POST /api/provisioning/orphaned/:hwUid/unclaim`, and the backend no longer
    // registers it. The legal recovery calls are reconcileOrphan (execute:false to inspect,
    // execute:true to retry the reclaim preserving mappings) and detachOrphan (deletes
    // mappings). unclaimGateway below is the unrelated confirmed unclaim of a claimed gateway.
    async reclaimOrphan(hwUid) {
        const { data } = await this.client.post(`/provisioning/orphaned/${hwUid}/reclaim`);
        return data;
    }
    async reconcileOrphan(hwUid, params) {
        const { data } = await this.client.post(`/provisioning/orphaned/${hwUid}/reconcile`, params ?? {});
        return data;
    }
    async detachOrphan(hwUid) {
        const { data } = await this.client.post(`/provisioning/orphaned/${hwUid}/detach`);
        return data;
    }
    // ===========================================================================
    // Gateways (Claimed) - Site-scoped
    // ===========================================================================
    async getGateways() {
        const { data } = await this.client.get(`${this.getSitePath()}/gateways`);
        const list = data.gateways ?? data;
        return Array.isArray(list) ? list.map((item) => normalizeGateway(item)) : [];
    }
    async getGateway(gatewayCode) {
        const url = `${this.getSitePath()}/gateways/${gatewayCode}`;
        const { data } = await this.client.get(url);
        const rawChannels = data.channels ?? [];
        const channels = Array.isArray(rawChannels)
            ? rawChannels.map((channel) => ({
                id: String(channel.id ?? channel.channelId ?? channel.channel_id ?? ''),
                channelId: String(channel.channelId ?? channel.channel_id ?? ''),
                channelName: String(channel.channelName ?? channel.channel_name ?? ''),
                channelType: String(channel.channelType ?? channel.channel_type ?? ''),
                properties: (channel.properties ?? {}),
                roomId: (channel.roomId ?? channel.room_id ?? null),
                chCode: (channel.chCode ?? channel.ch_code ?? null),
                chName: (channel.chName ?? channel.ch_name ?? null),
                currentValue: (channel.currentValue ?? channel.current_value ?? undefined),
                lastValueAt: (channel.lastValueAt ?? channel.last_value_at ?? null),
                pa: (channel.pa ?? null),
                chCodePa: (channel.chCodePa ?? channel.ch_code_pa ?? null),
                deviceId: (channel.deviceId ?? channel.device_id ?? null),
                deviceCode: (channel.deviceCode ?? channel.device_code ?? null),
                deviceName: (channel.deviceName ?? channel.device_name ?? null),
                available: channel.available !== false,
                hw: (channel.hw ?? null),
                grmsRole: isGrmsRole(channel.grmsRole ?? channel.grms_role)
                    ? (channel.grmsRole ?? channel.grms_role)
                    : null,
            }))
            : [];
        const rawDevices = data.devices ?? [];
        const devices = Array.isArray(rawDevices) ? rawDevices.map(mapGatewayDevice) : [];
        return {
            ...normalizeGateway(data),
            bleName: data.bleName,
            bleNameOverride: data.bleNameOverride ?? null,
            channels,
            devices,
        };
    }
    async updateGateway(gatewayCode, params) {
        const { data } = await this.client.patch(`${this.getSitePath()}/gateways/${gatewayCode}`, params);
        return data;
    }
    async updateChannel(gatewayCode, channelId, params) {
        const { data } = await this.client.patch(`${this.getSitePath()}/gateways/${gatewayCode}/channels/${channelId}`, params);
        return data;
    }
    async updateChannelName(gatewayCode, channelId, channelName) {
        return this.updateChannel(gatewayCode, channelId, { channelName });
    }
    async mapChannels(gatewayCode, mapping) {
        await this.client.post(`${this.getSitePath()}/gateways/${gatewayCode}/map`, mapping);
    }
    /**
     * `unclaim` is in Edge's proof-of-presence step-up set (`STEP_UP_SET` in
     * `src/middleware/presenceStepUp.ts`), so a claimed gateway refuses this
     * without an `X-Corteh-Presence` header. Real hardware answers the
     * challenge over BLE; there is no such out-of-band channel for a virtual
     * controller, so its presence header must be obtained through
     * `requestPresenceChallenge` + the emulator's `presenceAttestation` route
     * and passed here explicitly. Omit it for the ordinary hardware flow this
     * method already served before that header existed.
     */
    async unclaimGateway(gatewayCode, presenceHeader) {
        // `data: {}`: this instance always sends `Content-Type: application/json`
        // (see the constructor), and Fastify's JSON body parser refuses that
        // header on a request whose body is truly empty (FST_ERR_CTP_EMPTY_JSON_BODY).
        const { data } = await this.client.delete(`${this.getSitePath()}/gateways/${gatewayCode}/unclaim`, {
            headers: presenceHeader ? { 'x-corteh-presence': presenceHeader } : undefined,
            data: {},
        });
        return data;
    }
    /**
     * Mint a single-use proof-of-presence challenge for a step-up operation.
     * BMS-spec `docs/edge/rest-api.md` → "Proof-of-presence step-up v1".
     */
    async requestPresenceChallenge(gatewayCode, operation) {
        const { data } = await this.client.post(`${this.getSitePath()}/gateways/${gatewayCode}/presence-challenge`, { operation });
        return data;
    }
    async replaceGateway(gatewayCode, hwUid) {
        const { data } = await this.client.post(`${this.getSitePath()}/gateways/${gatewayCode}/replace`, { hwUid });
        return data;
    }
    async pushCryptoToGateway(gatewayCode) {
        const { data } = await this.client.post(`${this.getSitePath()}/gateways/${gatewayCode}/crypto`);
        return data;
    }
    async pushCryptoToAllGateways() {
        const { data } = await this.client.post(`${this.getSitePath()}/gateways/crypto/broadcast`);
        return data;
    }
    async getGatewayDevices(gatewayCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/gateways/${gatewayCode}/devices`);
        const list = data.devices ?? data;
        return Array.isArray(list) ? list.map((d) => mapGatewayDevice(d)) : [];
    }
    async updateGatewayDevice(gatewayCode, deviceCode, displayName) {
        const { data } = await this.client.patch(`${this.getSitePath()}/gateways/${gatewayCode}/devices/${deviceCode}`, { displayName });
        return data;
    }
    /** Forget an offline or missing peripheral; the server refuses an online one (409). */
    async removeGatewayDevice(gatewayCode, deviceCode) {
        const { data } = await this.client.delete(`${this.getSitePath()}/gateways/${gatewayCode}/devices/${deviceCode}`);
        return data;
    }
    /** Reset a peripheral through the configuration relay (BMS-spec config-relay.md "Reset and recover"); the outcome lands on GET config. */
    async resetGatewayDevice(gatewayCode, deviceCode, options = {}) {
        const { data } = await this.client.post(`${this.getSitePath()}/gateways/${gatewayCode}/devices/${deviceCode}/reset`, options);
        return data;
    }
    /** Settle a pending peripheral config job (BMS-spec rest-api.md POST …/config/jobs/:requestId/settle, config:admin). */
    async settleConfigJob(gatewayCode, deviceCode, requestId, body) {
        const { data } = await this.client.post(`${this.getSitePath()}/gateways/${gatewayCode}/devices/${deviceCode}/config/jobs/${requestId}/settle`, body);
        return data;
    }
    async getDeviceConfig(gatewayCode, deviceCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/gateways/${gatewayCode}/devices/${deviceCode}/config`);
        // Normalize schema array from snake_case (API may return raw DB JSON)
        const schema = Array.isArray(data.schema)
            ? data.schema.map((s) => {
                const labels = Array.isArray(s.labels) ? s.labels.map(String) : undefined;
                // BMS-spec rest-api.md "Device Config": an enum `def` may be an option label; it stands for that label's index.
                const def = typeof s.def === 'string' && labels?.includes(s.def) ? labels.indexOf(s.def) : Number(s.def ?? 0);
                return {
                    idx: Number(s.idx ?? 0),
                    name: String(s.name ?? ''),
                    label: String(s.label ?? s.name ?? ''),
                    type: String(s.type ?? 'uint16'),
                    ue: Boolean(s.ue ?? false),
                    def,
                    ...(typeof s.unit === 'string' && s.unit ? { unit: s.unit } : {}),
                    min: s.min != null ? Number(s.min) : undefined,
                    max: s.max != null ? Number(s.max) : undefined,
                    labels,
                };
            })
            : [];
        return {
            schema,
            values: data.values ?? {},
            ...(Array.isArray(data.endpoints) ? { endpoints: data.endpoints } : {}),
            ...(data.topicProfile && typeof data.topicProfile.r === 'number' ? { topicProfile: { r: data.topicProfile.r } } : {}),
            ...(data.requestId ? { requestId: String(data.requestId) } : {}),
            ...(data.result === 'applied' || data.result === 'rejected' ? { result: data.result } : {}),
            ...(data.message ? { message: String(data.message) } : {}),
            ...(data.pending === true ? { pending: true } : {}),
        };
    }
    /** The stored Manifest of a device (BMS-spec rest-api.md GET …/devices/:deviceCode/manifest); `null` when Edge holds none (404). */
    async getDeviceManifest(gatewayCode, deviceCode) {
        try {
            const { data } = await this.client.get(`${this.getSitePath()}/gateways/${gatewayCode}/devices/${deviceCode}/manifest`);
            return data;
        }
        catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 404)
                return null;
            throw error;
        }
    }
    async updateDeviceConfig(gatewayCode, deviceCode, params, saveAsDefaults) {
        const { data } = await this.client.patch(`${this.getSitePath()}/gateways/${gatewayCode}/devices/${deviceCode}/config`, { params, saveAsDefaults });
        return data;
    }
    // ===========================================================================
    // Rooms - Site-scoped
    // ===========================================================================
    /**
     * `fields: 'board'` asks for the room-board projection — the row without
     * `metadata` (GRMS §11.1 `fields` selector).
     */
    async getRooms(params) {
        // `ready` and `hasHomeGateway` are strict server-side filters applied before the rows are returned.
        const query = {};
        if (params?.fields)
            query.fields = params.fields;
        if (params?.ready !== undefined)
            query.ready = String(params.ready);
        if (params?.hasHomeGateway !== undefined)
            query.hasHomeGateway = String(params.hasHomeGateway);
        const { data } = await this.client.get(`${this.getSitePath()}/rooms`, Object.keys(query).length ? { params: query } : undefined);
        return data.rooms ?? data;
    }
    /**
     * Bulk Activate (BMS-spec rest-api.md #bulk-activate-rooms): `commissioning` → `active` for the
     * listed rooms that are Ready right now, one ordered result per code, refused rooms named with a
     * reason. 1–500 unique codes; never automatic.
     */
    async activateRooms(roomCodes) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/activate`, { roomCodes });
        return data;
    }
    async getRoomLabels() {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/labels`);
        return data.labels ?? [];
    }
    async getRoom(roomCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}`);
        return data;
    }
    async getRoomConfigurator(roomCode) {
        return (await this.client.get(`${this.getSitePath()}/rooms/${encodeURIComponent(roomCode)}/configurator`)).data;
    }
    async previewRoomConfigurator(roomCode, graph) {
        return (await this.client.post(`${this.getSitePath()}/rooms/${encodeURIComponent(roomCode)}/configurator/preview`, graph)).data;
    }
    async saveRoomConfigurator(roomCode, graph) {
        return (await this.client.put(`${this.getSitePath()}/rooms/${encodeURIComponent(roomCode)}/configurator`, graph)).data;
    }
    async getFunctionBindingSuggestions(roomCode, functionId, slotKey, options) {
        return (await this.client.get(`${this.getSitePath()}/rooms/${encodeURIComponent(roomCode)}/functions/${encodeURIComponent(functionId)}/slots/${encodeURIComponent(slotKey)}/suggestions`, { params: options })).data.suggestions;
    }
    async getTemplateFunctions(templateCode) {
        return (await this.client.get(`${this.getSitePath()}/templates/${encodeURIComponent(templateCode)}/functions`)).data;
    }
    async getTemplateRevisionDiff(templateCode, fromRevision, toRevision) {
        return (await this.client.get(`${this.getSitePath()}/templates/${encodeURIComponent(templateCode)}/revisions/diff`, { params: { fromRevision, toRevision } })).data;
    }
    async getTemplateRevisionImpact(templateCode, body) {
        return (await this.client.post(`${this.getSitePath()}/templates/${encodeURIComponent(templateCode)}/revisions/impact`, body)).data;
    }
    async createRoom(room) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms`, room);
        return data;
    }
    async updateRoom(roomCode, room) {
        const { data } = await this.client.put(`${this.getSitePath()}/rooms/${roomCode}`, room);
        return data;
    }
    async deleteRoom(roomCode) {
        await this.client.delete(`${this.getSitePath()}/rooms/${roomCode}`);
    }
    // ===========================================================================
    // Gateway-Room Associations - Site-scoped
    // ===========================================================================
    async getRoomGateways(roomCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}/gateways`);
        return data.gateways ?? data;
    }
    async associateGateway(_roomCode, _gatewayCode) {
        // Note: Gateway-room association is now implicit through channel mappings
        // This method is deprecated but kept for backward compatibility
        throw new Error('Gateway association is now implicit through channel mappings');
    }
    async dissociateGateway(_roomCode, _gatewayCode) {
        // Note: Gateway-room dissociation is now implicit through channel unmapping
        // This method is deprecated but kept for backward compatibility
        throw new Error('Gateway dissociation is now implicit through channel unmapping');
    }
    async getGatewayRooms(gatewayCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/gateways/${gatewayCode}/rooms`);
        return data.rooms ?? data;
    }
    async mapRoomChannels(roomCode, request) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/${roomCode}/map`, request);
        return data;
    }
    /** `chCode` is the gateway-unique channel code, not the gateway-local `channelId`. */
    async unmapRoomChannel(roomCode, chCode) {
        const { data } = await this.client.delete(`${this.getSitePath()}/rooms/${roomCode}/channels/${chCode}`);
        return data;
    }
    async removeRoomDeviceChannels(roomCode, deviceCode) {
        const { data } = await this.client.delete(`${this.getSitePath()}/rooms/${roomCode}/devices/${deviceCode}/channels`);
        return data;
    }
    // ===========================================================================
    // Access zone channel mapping — the zone half of channel containment
    // ===========================================================================
    async mapZoneChannels(zoneCode, request) {
        const { data } = await this.client.post(`${this.getSitePath()}/zones/${zoneCode}/map`, request);
        return data;
    }
    async unmapZoneChannel(zoneCode, channelId) {
        const { data } = await this.client.delete(`${this.getSitePath()}/zones/${zoneCode}/channels/${channelId}`);
        return data;
    }
    async getZoneChannels(zoneCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/zones/${zoneCode}/channels`);
        return data.channels ?? data;
    }
    async getZoneChannel(zoneCode, chCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/zones/${zoneCode}/channels/${chCode}`);
        return data;
    }
    async sendZoneChannelCommand(zoneCode, chCode, command) {
        await this.client.post(`${this.getSitePath()}/zones/${zoneCode}/channels/${chCode}/cmd`, command);
    }
    // ===========================================================================
    // Channels - Site-scoped
    // ===========================================================================
    async getChannels(roomCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}/channels`);
        return data.channels ?? data;
    }
    async getChannel(roomCode, chCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}/channels/${chCode}`);
        return data;
    }
    async sendCommand(roomCode, chCode, command) {
        await this.client.post(`${this.getSitePath()}/rooms/${roomCode}/channels/${chCode}/cmd`, command);
    }
    async getChannelHistory(roomCode, chCode, params) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}/channels/${chCode}/history`, { params });
        return data;
    }
    // ===========================================================================
    // System
    // ===========================================================================
    async getVersion() {
        const { data } = await this.client.get('/version');
        return data;
    }
    async getHealth() {
        // `/health` deliberately lives outside the authenticated `/api` surface.
        const { data } = await axios.get(`${this.config.baseUrl}/health`, {
            timeout: this.config.timeout,
            ...(this.config.fetch ? { adapter: 'fetch', env: { fetch: this.config.fetch } } : {}),
        });
        return data;
    }
    async getDiagnostics() {
        const { data } = await this.client.get('/system/health');
        return data;
    }
    async triggerStaleCheck() {
        const { data } = await this.client.post('/system/stale-check');
        return data;
    }
    async flushCache() {
        const { data } = await this.client.post('/system/flush-cache');
        return data;
    }
    async getBranding() {
        const { data } = await this.client.get('/site/branding');
        return data;
    }
    async uploadBrandingLogo(file) {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await this.client.post('/site/branding/logo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    }
    async exportConfig() {
        const { data } = await this.client.get('/system/export');
        return data;
    }
    // ===========================================================================
    // Data Export (CSV / JSON)
    // ===========================================================================
    async exportRoomsCsv(site) {
        const { data } = await this.client.get(`/sites/${site}/export/rooms`, { responseType: 'text' });
        return data;
    }
    async exportChannelsCsv(site) {
        const { data } = await this.client.get(`/sites/${site}/export/channels`, { responseType: 'text' });
        return data;
    }
    async exportGatewaysCsv(site) {
        const { data } = await this.client.get(`/sites/${site}/export/gateways`, { responseType: 'text' });
        return data;
    }
    async exportSiteConfig(site) {
        const { data } = await this.client.get(`/sites/${site}/export/config`);
        return data;
    }
    // ===========================================================================
    // Event / Audit Log
    // ===========================================================================
    async getEvents(params) {
        const { data } = await this.client.get('/events', { params });
        return data;
    }
    async getEventStats() {
        const { data } = await this.client.get('/events/stats');
        return data;
    }
    async purgeEvents(params) {
        const { data } = await this.client.delete('/events/purge', { data: params });
        return data;
    }
    async getCommissioningGaps() {
        const { data } = await this.client.get(`${this.getSitePath()}/commissioning/gaps`);
        return data;
    }
    // ===========================================================================
    // Room Templates - Site-scoped
    // ===========================================================================
    get roomConfigurator() { return createRoomConfiguratorApi(this.client, () => this.getSitePath()); }
    async getRoomTemplates() {
        const { data } = await this.client.get(`${this.getSitePath()}/templates`);
        return data.templates ?? data;
    }
    async getRoomTemplate(templateCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/templates/${templateCode}`);
        return data;
    }
    async createRoomTemplate(params) {
        assertSceneIconsForAuthoring(params.scenes);
        const { data } = await this.client.post(`${this.getSitePath()}/templates`, params);
        return data;
    }
    async updateRoomTemplate(templateCode, params) {
        // Validate only when scenes are explicitly edited. An unrelated partial
        // edit must not rewrite or refuse a legacy unknown stored icon.
        assertSceneIconsForAuthoring(params.scenes);
        const { data } = await this.client.put(`${this.getSitePath()}/templates/${templateCode}`, params);
        return data;
    }
    async deleteRoomTemplate(templateCode, expectedRevision) {
        await this.client.delete(`${this.getSitePath()}/templates/${templateCode}`, { params: { expectedRevision } });
    }
    async createRoomTemplateFromRoom(roomCode, params) {
        const { data } = await this.client.post(`${this.getSitePath()}/templates/from-room/${roomCode}`, params);
        return data;
    }
    async applyRoomTemplate(roomCode, params) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/${roomCode}/apply-template`, params);
        return data;
    }
    async reapplyRoomTemplate(templateCode, body) {
        const { data } = await this.client.post(`${this.getSitePath()}/templates/${templateCode}/reapply`, body);
        return data;
    }
    // BMS-spec rest-api.md #template-kit-api, #template-transfer, #room-finish.
    async getTemplateKit(templateCode, revision) {
        const { data } = await this.client.get(`${this.getSitePath()}/templates/${encodeURIComponent(templateCode)}/kit`, { params: revision === undefined ? {} : { revision } });
        return data;
    }
    async putTemplateKit(templateCode, body) {
        const { data } = await this.client.put(`${this.getSitePath()}/templates/${encodeURIComponent(templateCode)}/kit`, body);
        return data;
    }
    async validateTemplateKit(templateCode, body) {
        const { data } = await this.client.post(`${this.getSitePath()}/templates/${encodeURIComponent(templateCode)}/kit/validate`, body);
        return data;
    }
    async captureTemplateKit(templateCode, body) {
        const { data } = await this.client.post(`${this.getSitePath()}/templates/${encodeURIComponent(templateCode)}/kit/capture`, body);
        return data;
    }
    async exportRoomTemplate(templateCode, revision) {
        const { data } = await this.client.get(`${this.getSitePath()}/templates/${encodeURIComponent(templateCode)}/export`, { params: revision === undefined ? {} : { revision } });
        return data;
    }
    async importRoomTemplate(body) {
        const { data } = await this.client.post(`${this.getSitePath()}/templates/import`, body);
        return data;
    }
    async getRoomFinish(roomCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${encodeURIComponent(roomCode)}/finish`);
        return data;
    }
    async startRoomFinish(roomCode, expectedVersion) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/${encodeURIComponent(roomCode)}/finish`, { expectedVersion });
        return data;
    }
    async detachRoomTemplate(roomCode, expectedVersion) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/${roomCode}/detach-template`, { expectedVersion });
        return data;
    }
    // ===========================================================================
    // Gateway Templates - Site-scoped
    // ===========================================================================
    async getGatewayTemplates() {
        const { data } = await this.client.get(`${this.getSitePath()}/gateway-templates`);
        return data.templates ?? data;
    }
    async getGatewayTemplate(templateCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/gateway-templates/${templateCode}`);
        return data;
    }
    async createGatewayTemplate(params) {
        const { data } = await this.client.post(`${this.getSitePath()}/gateway-templates`, params);
        return data;
    }
    async updateGatewayTemplate(templateCode, params) {
        const { data } = await this.client.patch(`${this.getSitePath()}/gateway-templates/${templateCode}`, params);
        return data;
    }
    async deleteGatewayTemplate(templateCode) {
        await this.client.delete(`${this.getSitePath()}/gateway-templates/${templateCode}`);
    }
    async applyGatewayTemplate(gatewayCode, templateCode) {
        const { data } = await this.client.post(`${this.getSitePath()}/gateways/${gatewayCode}/apply-gateway-template/${templateCode}`);
        return data;
    }
    // ===========================================================================
    // Room Params - Site-scoped
    // ===========================================================================
    async getRoomParams(roomCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}/params`);
        return data;
    }
    async updateRoomParams(roomCode, values) {
        const { data } = await this.client.patch(`${this.getSitePath()}/rooms/${roomCode}/params`, { values });
        return data;
    }
    /** BMS-spec hotel-relative-thermostat.md: bound zones with durable policy record and observed values. */
    async getHotelThermostat(roomCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}/hotel-thermostat`);
        return data;
    }
    /** Bind a canonical room zone to a commissioned hotel thermostat channel. */
    async bindHotelThermostat(roomCode, zone, chCode) {
        const { data } = await this.client.put(`${this.getSitePath()}/rooms/${roomCode}/hotel-thermostat/zones/${zone}`, { chCode });
        return data;
    }
    async unbindHotelThermostat(roomCode, zone) {
        await this.client.delete(`${this.getSitePath()}/rooms/${roomCode}/hotel-thermostat/zones/${zone}`);
    }
    /**
     * Clear a rejected or conflicting record by adopting the device's own confirmed values. A stay reset
     * that is still owed is refused (409) unless `discardOwedStay` says so.
     */
    async acknowledgeHotelThermostat(roomCode, zone, options = {}) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/${roomCode}/hotel-thermostat/zones/${zone}/acknowledge`, options);
        return data;
    }
    /** Resend a pending policy tuple (a rejected one under a fresh request id); still pending until a correlated answer. */
    async retryHotelThermostat(roomCode, zone) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/${roomCode}/hotel-thermostat/zones/${zone}/retry`);
        return data;
    }
    async setRoomCustomSchema(roomCode, params) {
        const { data } = await this.client.put(`${this.getSitePath()}/rooms/${roomCode}/params/custom`, { params });
        return data;
    }
    async getRoomBindings(roomCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}/params/bindings`);
        return data;
    }
    async setRoomBindings(roomCode, bindings) {
        const { data } = await this.client.put(`${this.getSitePath()}/rooms/${roomCode}/params/bindings`, { bindings });
        return data;
    }
    async getRoomBindingSuggestions(roomCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}/params/suggest`);
        return data;
    }
    // ===========================================================================
    // Room Scenes - Site-scoped
    // ===========================================================================
    async getRoomScenes(roomCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}/scenes`);
        return data.scenes ?? data;
    }
    async createScene(roomCode, params) {
        assertSceneIconsForAuthoring([params], 'scene');
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/${roomCode}/scenes`, params);
        return data;
    }
    async updateScene(roomCode, sceneCode, params) {
        if ('icon' in params)
            assertSceneIconsForAuthoring([params], 'scene');
        const { data } = await this.client.put(`${this.getSitePath()}/rooms/${roomCode}/scenes/${sceneCode}`, params);
        return data;
    }
    async deleteScene(roomCode, sceneCode) {
        await this.client.delete(`${this.getSitePath()}/rooms/${roomCode}/scenes/${sceneCode}`);
    }
    async activateScene(roomCode, sceneCode) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/${roomCode}/scenes/${sceneCode}/activate`);
        return data;
    }
    // ===========================================================================
    // Site floor plans - Site-scoped
    // ===========================================================================
    async getRoomLayouts(type) {
        const params = type ? `?type=${type}` : '';
        const { data } = await this.client.get(`${this.getSitePath()}/room-layouts${params}`);
        return data.templates;
    }
    async getRoomLayout(templateCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/room-layouts/${templateCode}`);
        return data;
    }
    async createRoomLayout(params) {
        const { data } = await this.client.post(`${this.getSitePath()}/room-layouts`, params);
        return data;
    }
    async updateRoomLayout(templateCode, params) {
        const { data } = await this.client.put(`${this.getSitePath()}/room-layouts/${templateCode}`, params);
        return data;
    }
    async deleteRoomLayout(templateCode) {
        await this.client.delete(`${this.getSitePath()}/room-layouts/${templateCode}`);
    }
    async duplicateRoomLayout(templateCode, newName) {
        const { data } = await this.client.post(`${this.getSitePath()}/room-layouts/${templateCode}/duplicate`, {
            templateName: newName,
        });
        return data;
    }
    async importRoomLayout(yaml) {
        const { data } = await this.client.post(`${this.getSitePath()}/room-layouts/import`, { yaml });
        return data;
    }
    async exportRoomLayout(templateCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/room-layouts/${templateCode}/export`);
        return data.yaml ?? data;
    }
    // ===========================================================================
    // Channel Groups - Site-scoped
    // ===========================================================================
    async updateRoomGroups(roomCode, groups) {
        const { data } = await this.client.put(`${this.getSitePath()}/rooms/${roomCode}/groups`, { groups });
        return data;
    }
    async getScenes(roomCode) {
        return this.getRoomScenes(roomCode);
    }
    async updateChannelGroup(roomCode, channelId, groupCode, sortOrder) {
        const { data } = await this.client.put(`${this.getSitePath()}/rooms/${roomCode}/channels/${channelId}/group`, { groupCode, sortOrder });
        return data;
    }
    async setChannelGroup(roomCode, channelId, params) {
        const { data } = await this.client.put(`${this.getSitePath()}/rooms/${roomCode}/channels/${channelId}/group`, params);
        return data;
    }
    async bulkUpdateChannelGroups(roomCode, assignments) {
        const { data } = await this.client.put(`${this.getSitePath()}/rooms/${roomCode}/channels/groups`, { assignments });
        return data;
    }
    // ===========================================================================
    // Bulk Import - Site-scoped
    // ===========================================================================
    async bulkImportPreview(rows) {
        const { data } = await this.client.post(`${this.getSitePath()}/bulk/rooms/preview`, { rows });
        return data;
    }
    async bulkImportExecute(rows) {
        const { data } = await this.client.post(`${this.getSitePath()}/bulk/rooms/execute`, { rows });
        return data;
    }
    async previewBulkImport(rows) {
        return this.bulkImportPreview(rows);
    }
    async executeBulkImport(rows) {
        return this.bulkImportExecute(rows);
    }
    // ===========================================================================
    // Notifications
    // ===========================================================================
    async getNotifications(params) {
        const { data } = await this.client.get(`${this.getSitePath()}/notifications`, { params });
        return data;
    }
    async getUnreadCount() {
        const { data } = await this.client.get(`${this.getSitePath()}/notifications/unread-count`);
        return data;
    }
    async markNotificationRead(id) {
        const { data } = await this.client.patch(`${this.getSitePath()}/notifications/${id}/read`);
        return data;
    }
    async markAllNotificationsRead() {
        const { data } = await this.client.post(`${this.getSitePath()}/notifications/mark-all-read`);
        return data;
    }
    async deleteNotification(id) {
        const { data } = await this.client.delete(`${this.getSitePath()}/notifications/${id}`);
        return data;
    }
    // ===========================================================================
    // Alert Rules
    // ===========================================================================
    async getAlertRules() {
        const { data } = await this.client.get(`${this.getSitePath()}/alert-rules`);
        return data;
    }
    async createAlertRule(params) {
        const { data } = await this.client.post(`${this.getSitePath()}/alert-rules`, params);
        return data;
    }
    async updateAlertRule(id, params) {
        const { data } = await this.client.put(`${this.getSitePath()}/alert-rules/${id}`, params);
        return data;
    }
    async deleteAlertRule(id) {
        const { data } = await this.client.delete(`${this.getSitePath()}/alert-rules/${id}`);
        return data;
    }
    // ===========================================================================
    // Access Control — Locks
    // ===========================================================================
    async getLocks(filters) {
        const { data } = await this.client.get(`${this.getSitePath()}/locks`, { params: filters });
        return data.locks ?? data;
    }
    async getLock(lockCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/locks/${lockCode}`);
        return data;
    }
    async createLock(lock) {
        const { data } = await this.client.post(`${this.getSitePath()}/locks`, lock);
        return data;
    }
    async updateLock(lockCode, params) {
        const { data } = await this.client.put(`${this.getSitePath()}/locks/${lockCode}`, params);
        return data;
    }
    async deleteLock(lockCode) {
        const { data } = await this.client.delete(`${this.getSitePath()}/locks/${lockCode}`);
        return data;
    }
    // ---------------------------------------------------------------------------
    // Manual lock actions
    //
    // BMS-spec `docs/edge/rest-api.md` §"Manual lock actions". Three routes, one
    // meaning each — the action is the URL, never a field in the body:
    //
    //   lock   → Relock          `switch: true`
    //   unlock → held Unlock     `switch: false`   (stays unlocked)
    //   open   → momentary Open  `pulse_ms: N`     (relocks itself)
    //
    // Unlock and Open are genuinely different operations, not two spellings of
    // one: a guest let in through a held Unlock stays let in until someone
    // relocks, while an Open closes behind them. Collapsing them — as the client
    // used to, by pointing an "Open" control at `/unlock` — leaves a door
    // standing unlocked that the operator believes pulsed shut.
    //
    // Each body is an empty object: duration is the lock's configuration, and any
    // nonempty body (`action`, `pulseMs`, `hold`, `switch`, …) is
    // `400 invalid_request`. Which actions a lock accepts is its `lock_mode`;
    // an unsupported pair is refused rather than substituted.
    // ---------------------------------------------------------------------------
    /** Relock: `switch: true`. Refused on a pulse-only lock. */
    async lockLock(lockCode) {
        const { data } = await this.client.post(`${this.getSitePath()}/locks/${lockCode}/lock`, {});
        return data;
    }
    /** Held unlock: `switch: false`. The lock stays unlocked until relocked. */
    async unlockLock(lockCode) {
        const { data } = await this.client.post(`${this.getSitePath()}/locks/${lockCode}/unlock`, {});
        return data;
    }
    /**
     * Momentary open: `pulse_ms: N` from the lock's configured duration.
     *
     * A lost response is an unknown submission outcome, so a caller refetches and
     * reconciles; it MUST NOT automatically repeat an Open, which would open the
     * door a second time.
     */
    async openLock(lockCode) {
        const { data } = await this.client.post(`${this.getSitePath()}/locks/${lockCode}/open`, {});
        return data;
    }
    /**
     * Unlock every active lock in the site, or narrow to one building or floor.
     * The scope is resolved from whichever container holds each lock — a room or
     * an access zone — so a corridor or stairwell lock is reached too.
     */
    async emergencyUnlock(scope) {
        const { data } = await this.client.post(`${this.getSitePath()}/locks/emergency-unlock`, scope ?? {});
        return data;
    }
    async getLockEvents(filters) {
        const { data } = await this.client.get(`${this.getSitePath()}/locks/events`, {
            params: lockEventQuery(filters),
        });
        return data;
    }
    async getLockEventsByLock(lockCode, filters) {
        const { data } = await this.client.get(`${this.getSitePath()}/locks/${lockCode}/events`, {
            params: lockEventQuery(filters),
        });
        return data;
    }
    // ===========================================================================
    // Access Control — Zones (access-control-only locations)
    // ===========================================================================
    async getZones(filters) {
        const { data } = await this.client.get(`${this.getSitePath()}/zones`, { params: filters });
        return data.zones ?? data;
    }
    async getZone(zoneCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/zones/${zoneCode}`);
        return data;
    }
    async createZone(zone) {
        const { data } = await this.client.post(`${this.getSitePath()}/zones`, zone);
        return data.zone ?? data;
    }
    async updateZone(zoneCode, params) {
        const { data } = await this.client.put(`${this.getSitePath()}/zones/${zoneCode}`, params);
        return data;
    }
    async deleteZone(zoneCode) {
        const { data } = await this.client.delete(`${this.getSitePath()}/zones/${zoneCode}`);
        return data;
    }
    // ===========================================================================
    // Access Control — Readers (many-to-many reader-lock links)
    // ===========================================================================
    async getReaders() {
        const { data } = await this.client.get(`${this.getSitePath()}/readers`);
        return data.readers ?? data;
    }
    async upsertReader(params) {
        const { data } = await this.client.post(`${this.getSitePath()}/readers`, params);
        return data;
    }
    async getReaderLocks(readerChannelId) {
        const { data } = await this.client.get(`${this.getSitePath()}/readers/${readerChannelId}/locks`);
        return data.locks ?? data;
    }
    async linkReaderToLock(readerChannelId, lockCode, role) {
        const { data } = await this.client.post(`${this.getSitePath()}/readers/${readerChannelId}/link`, { lockCode, role: role ?? 'entry' });
        return data;
    }
    async unlinkReaderFromLock(readerChannelId, lockCode, role) {
        const { data } = await this.client.delete(`${this.getSitePath()}/readers/${readerChannelId}/locks/${lockCode}`, { params: role ? { role } : undefined });
        return data;
    }
    async getLockReaders(lockCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/locks/${lockCode}/readers`);
        return data.readers ?? data;
    }
    // ===========================================================================
    // Access Cards & Lock Groups
    // ===========================================================================
    async getAccessCards(filters) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/cards`, { params: filters });
        return data.cards ?? data;
    }
    /** Active guest keys per room, counted by Edge (no card page). Needs `cards:read`. */
    async getGuestKeySummary() {
        const { data } = await this.client.get(`${this.getSitePath()}/access/cards/guest-key-summary`);
        return data.rooms ?? [];
    }
    async getAccessCard(cardId) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/cards/${cardId}`);
        // Backend returns { card: {...}, events: [...] } — flatten to AccessCardDetail
        return { ...data.card, events: data.events ?? [] };
    }
    async getAccessCardByUid(cardUid) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/cards/by-uid/${cardUid}`);
        return { ...data.card, events: data.events ?? [] };
    }
    async issueCard(params) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/cards`, params);
        return data.card ?? data;
    }
    async getIssueCardPayload(params) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/cards/payload`, params);
        return data;
    }
    async replaceCard(cardId, params) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/cards/${cardId}/replace`, params);
        return data.card ?? data;
    }
    async getReplaceCardPayload(cardId, params) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/cards/${cardId}/replace/payload`, params);
        return data;
    }
    async updateCard(cardId, params) {
        const { data } = await this.client.patch(`${this.getSitePath()}/access/cards/${cardId}`, params);
        return data;
    }
    async revokeCard(cardId, reason) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/cards/${cardId}/revoke`, { reason });
        return data;
    }
    async suspendCard(cardId, reason) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/cards/${cardId}/suspend`, { reason });
        return data;
    }
    async reactivateCard(cardId) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/cards/${cardId}/reactivate`);
        return data;
    }
    async retireCard(cardId) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/cards/${cardId}/retire`);
        return data;
    }
    async reportLostCard(cardId) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/cards/${cardId}/report-lost`);
        return data;
    }
    /** Confirm a re-encode with the encoder's write report. */
    async confirmCardWritten(cardId, writeReport) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/cards/${cardId}/written`, writeReport);
        return data;
    }
    /** The signed image for re-encoding a card, sized for the carrier the encoder detected. */
    async getCardPayload(cardId, chipType) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/cards/${cardId}/payload`, { chipType });
        return data;
    }
    async getRoomCards(roomCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}/cards`);
        return data.cards ?? data;
    }
    async revokeAllRoomCards(roomCode, reason) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/${roomCode}/cards/revoke-all`, { reason });
        return data;
    }
    // --- SAM Enrollment ---
    async getSamMaterial() {
        const { data } = await this.client.get(`${this.getSitePath()}/access/sam/material`);
        return data;
    }
    // --- Server Signing Keys (TPM2 / Software provider) ---
    async getServerSigningKeys() {
        const { data } = await this.client.get(`${this.getSitePath()}/access/keys`);
        return data;
    }
    // --- Site package ---
    /**
     * "Activate a site" on an installation with no site yet (`keys:manage` and
     * `site:write`): a setup code, a restore code, or the Linda site file. Creates
     * the site; the response carries its code and never any key, file or backup bytes.
     */
    async activateInstallationSite(body) {
        const { data } = await this.client.post('/access/site-activation', body);
        return data;
    }
    /**
     * Import a Linda site file into this site (`keys:manage`). A card-key rotation
     * starts and finishes by importing files; the response never carries the envelope.
     */
    async importSitePackage(body) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/site-package`, body);
        return data;
    }
    async checkSitePackage() {
        const { data } = await this.client.post(`${this.getSitePath()}/access/site-package/check`, {});
        return data;
    }
    /** "Back up now": upload the configuration to Linda if it changed. Metadata only. */
    async backupSiteConfiguration() {
        const { data } = await this.client.post(`${this.getSitePath()}/access/backup`, {});
        return data;
    }
    // --- Card Role Templates ---
    async getCardRoles() {
        const { data } = await this.client.get(`${this.getSitePath()}/access/roles`);
        return data.roles ?? data;
    }
    async getCardRole(roleName) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/roles/${roleName}`);
        return data;
    }
    async createCardRole(params) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/roles`, params);
        return data;
    }
    async updateCardRole(roleName, params) {
        const { data } = await this.client.put(`${this.getSitePath()}/access/roles/${roleName}`, params);
        return data;
    }
    async deleteCardRole(roleName) {
        const { data } = await this.client.delete(`${this.getSitePath()}/access/roles/${roleName}`);
        return data;
    }
    async applyRole(roleName) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/roles/${roleName}/apply`);
        return data;
    }
    // --- Lock Groups ---
    async getLockGroups() {
        const { data } = await this.client.get(`${this.getSitePath()}/access/groups`);
        return data.groups ?? data;
    }
    async getLockGroup(groupCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/groups/${groupCode}`);
        return data;
    }
    async createLockGroup(params) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/groups`, params);
        return data;
    }
    async updateLockGroup(groupCode, params) {
        const { data } = await this.client.put(`${this.getSitePath()}/access/groups/${groupCode}`, params);
        return data;
    }
    async deleteLockGroup(groupCode) {
        const { data } = await this.client.delete(`${this.getSitePath()}/access/groups/${groupCode}`);
        return data;
    }
    // --- Access Schedules ---
    //
    // Attachments are replaced as a SET, not added and removed one at a time: the
    // request states exactly what the lock or card carries when it returns, and a
    // lock PUT republishes reader context before it resolves.
    async getAccessSchedules() {
        const { data } = await this.client.get(`${this.getSitePath()}/access/schedules`);
        return data.schedules ?? data;
    }
    async getAccessSchedule(scheduleCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/schedules/${scheduleCode}`);
        return data;
    }
    async createAccessSchedule(params) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/schedules`, params);
        return data;
    }
    async updateAccessSchedule(scheduleCode, params) {
        const { data } = await this.client.put(`${this.getSitePath()}/access/schedules/${scheduleCode}`, params);
        return data;
    }
    async deleteAccessSchedule(scheduleCode) {
        const { data } = await this.client.delete(`${this.getSitePath()}/access/schedules/${scheduleCode}`);
        return data;
    }
    /**
     * Replace only a schedule's window list.
     *
     * The narrow counterpart of `updateAccessSchedule`, under its own permission
     * `access_control:write_schedule_windows`. The body carries `windows` and
     * **nothing else**: the route is `.strict()`, so naming `enabled`,
     * `scheduleName`, `description` or an attachment is `400 invalid_request`
     * rather than a silently dropped key, and an empty list is refused the same
     * way because "no hours" closes a door entirely and that stays with
     * `access_control:write`. More than `MAX_SCHEDULE_WINDOWS` is
     * `422 schedule_window_limit`, an unknown code is `404 schedule_not_found`,
     * and the answer is the schedule detail with reader context already
     * republished.
     *
     * BMS-spec `docs/access/schedules.md` §Editing windows without owning the
     * schedule; `docs/grms/specification.md` §14.2.
     */
    async updateAccessScheduleWindows(scheduleCode, windows) {
        const { data } = await this.client.patch(`${this.getSitePath()}/access/schedules/${scheduleCode}/windows`, { windows });
        return data;
    }
    async getLockSchedules(lockCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/locks/${lockCode}/schedules`);
        return data.attachments ?? data;
    }
    async setLockSchedules(lockCode, attachments) {
        const { data } = await this.client.put(`${this.getSitePath()}/access/locks/${lockCode}/schedules`, { attachments });
        return data.attachments ?? data;
    }
    async getCardSchedules(cardId) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/cards/${cardId}/schedules`);
        return data.attachments ?? data;
    }
    async setCardSchedules(cardId, scheduleCodes) {
        const { data } = await this.client.put(`${this.getSitePath()}/access/cards/${cardId}/schedules`, { scheduleCodes });
        return data.attachments ?? data;
    }
    // --- Visitors ---
    //
    // The visitor's CARD is not here: it is issued with `issueCard({ visitId })`,
    // and check-out revokes the visit's live cards through this API without
    // retiring them.
    async getVisitors(filters) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/visitors`, { params: filters });
        return data;
    }
    async getVisitor(visitorId) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/visitors/${visitorId}`);
        return data;
    }
    async createVisitor(params) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/visitors`, params);
        return data;
    }
    async updateVisitor(visitorId, params) {
        const { data } = await this.client.patch(`${this.getSitePath()}/access/visitors/${visitorId}`, params);
        return data;
    }
    /**
     * Erase a person. Refused with `visit_has_live_cards` while they hold a card
     * record that is not retired; revoke and retire the credential first.
     */
    async eraseVisitor(visitorId) {
        const { data } = await this.client.delete(`${this.getSitePath()}/access/visitors/${visitorId}`);
        return data;
    }
    /** With no range, this returns the visits scheduled for the current site-local day. */
    async getVisits(filters) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/visits`, { params: filters });
        return data;
    }
    async getVisit(visitId) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/visits/${visitId}`);
        return data;
    }
    async createVisit(params) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/visits`, params);
        return data;
    }
    /**
     * Change host, purpose, or the expected window. This never touches a card
     * record: the card's window is signed onto it, so widening access is
     * `updateCard({ validUntil })`, which returns `requiresRewrite: true` and is
     * followed by a re-encode.
     */
    async updateVisit(visitId, params) {
        const { data } = await this.client.patch(`${this.getSitePath()}/access/visits/${visitId}`, params);
        return data;
    }
    async checkInVisit(visitId) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/visits/${visitId}/check-in`);
        return data;
    }
    /** Revokes the visit's live cards; it does not retire them. */
    async checkOutVisit(visitId) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/visits/${visitId}/check-out`);
        return data;
    }
    async cancelVisit(visitId) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/visits/${visitId}/cancel`);
        return data;
    }
    // ===========================================================================
    // Access Analytics — read-only, `access_analytics:read`
    //
    // Seven reports over data the platform already produces. Every one returns the
    // same envelope naming the range, the timezone, and whether rollups or raw
    // events backed the numbers. A range extending before retention is clamped
    // rather than rejected, and `range.clamped` says so.
    // BMS-spec docs/access/analytics.md §Analytics API.
    // ===========================================================================
    async getAccessAnalyticsSummary(query) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/analytics/summary`, { params: query });
        return data;
    }
    async getAccessAnalyticsTimeseries(query) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/analytics/timeseries`, { params: query });
        return data;
    }
    async getAccessAnalyticsLocks(query) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/analytics/locks`, { params: query });
        return data;
    }
    async getAccessAnalyticsCards(query) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/analytics/cards`, { params: query });
        return data;
    }
    async getAccessAnalyticsDenials(query) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/analytics/denials`, { params: query });
        return data;
    }
    async getAccessAnalyticsVisitors(query) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/analytics/visitors`, { params: query });
        return data;
    }
    async getAccessAnomalies(query) {
        const { data } = await this.client.get(`${this.getSitePath()}/access/analytics/anomalies`, { params: query });
        return data;
    }
    async addLocksToGroup(groupCode, lockCodes) {
        const { data } = await this.client.post(`${this.getSitePath()}/access/groups/${groupCode}/locks`, { lockCodes });
        return data;
    }
    async removeLocksFromGroup(groupCode, lockCodes) {
        const { data } = await this.client.delete(`${this.getSitePath()}/access/groups/${groupCode}/locks`, { data: { lockCodes } });
        return data;
    }
    // ===========================================================================
    // GRMS — Room Occupancy & Housekeeping
    // ===========================================================================
    async updateOccupancy(roomCode, params) {
        const { data } = await this.client.patch(`${this.getSitePath()}/rooms/${roomCode}/occupancy`, params);
        return data;
    }
    async moveStay(roomCode, targetRoomCode) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/${roomCode}/move`, {
            targetRoomCode,
        });
        return data;
    }
    /**
     * Acknowledge a Make Up Room request (GRMS spec section 11.4). The room's own
     * button stays authoritative: this records `mur_acknowledged` and enqueues the
     * durable hardware clear, and answers 202 `pending_hardware_clear`. It does
     * not clear the flag, so callers must not treat it as an off switch.
     */
    async acknowledgeMur(roomCode) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/${roomCode}/mur/acknowledge`);
        return data;
    }
    async getRoomEvents(roomCode, params) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}/events`, { params });
        return data;
    }
    /** A note on the room's log: `room_events.event_type = 'note'` (GRMS §11.2). */
    async addRoomNote(roomCode, note) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/${roomCode}/events`, { event_type: 'note', metadata: { note } });
        return data;
    }
    async getRoomHistory(roomCode, params) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}/history`, { params });
        return data;
    }
    async getGrmsAnalytics(days = 30) {
        const { data } = await this.client.get(`${this.getSitePath()}/grms/analytics`, {
            params: { days },
        });
        return data;
    }
    async getPmsConnection() {
        const { data } = await this.client.get(`${this.getSitePath()}/pms/config`);
        return data;
    }
    async updatePmsConnection(update) {
        const { data } = await this.client.put(`${this.getSitePath()}/pms/config`, update);
        return data;
    }
    async getOccupancySummary() {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/summary`);
        return data;
    }
    async getTodayStays() {
        const { data } = await this.client.get(`${this.getSitePath()}/grms/stays`, {
            params: { window: 'today' },
        });
        return data;
    }
    async updateHousekeeping(roomCode, params) {
        const { data } = await this.client.patch(`${this.getSitePath()}/rooms/${roomCode}/housekeeping`, params);
        return data;
    }
    async reportMaintenanceIssue(roomCode, params) {
        const { data } = await this.client.post(`${this.getSitePath()}/rooms/${roomCode}/maintenance`, params);
        return data;
    }
    /**
     * Set or clear the service overlay. Clearing it restores whatever
     * occupancy × housekeeping pair the room already held — the occupancy
     * endpoint no longer accepts the overlay values (spec §3.1, §6.3).
     */
    async updateServiceState(roomCode, params) {
        const { data } = await this.client.patch(`${this.getSitePath()}/rooms/${roomCode}/service-state`, params);
        return data;
    }
    async getHousekeepingSummary() {
        const { data } = await this.client.get(`${this.getSitePath()}/housekeeping/summary`);
        return data;
    }
    async batchAssignHousekeeping(params) {
        const { data } = await this.client.post(`${this.getSitePath()}/housekeeping/assign`, params);
        return data;
    }
    // ===========================================================================
    // Alarms - Site-scoped
    // ===========================================================================
    async getActiveAlarms() {
        const { data } = await this.client.get(`${this.getSitePath()}/alarms`);
        return data.alarms ?? data;
    }
    async getRoomAlarms(roomCode) {
        const { data } = await this.client.get(`${this.getSitePath()}/rooms/${roomCode}/alarms`);
        return data.alarms ?? data;
    }
    /**
     * Alarm history over a date range — every status, not just the open ones.
     * BMS-spec `docs/edge/rest-api.md` §"Operational room alarms": the window is
     * half-open `[from, to)` on `created_at` and defaults to the last 30 days.
     */
    async getAlarmHistory(params) {
        const { data } = await this.client.get(`${this.getSitePath()}/alarms/history`, { params });
        return data;
    }
    async acknowledgeAlarm(alarmId) {
        const { data } = await this.client.post(`${this.getSitePath()}/alarms/${alarmId}/acknowledge`);
        return data;
    }
    async resolveAlarm(alarmId) {
        const { data } = await this.client.post(`${this.getSitePath()}/alarms/${alarmId}/resolve`);
        return data;
    }
    // ===========================================================================
    // Schedules - Site-scoped
    // ===========================================================================
    async getSchedules() {
        const { data } = await this.client.get(`${this.getSitePath()}/schedules`);
        return data.schedules ?? data;
    }
    async getSchedule(id) {
        const { data } = await this.client.get(`${this.getSitePath()}/schedules/${id}`);
        return data;
    }
    async createSchedule(params) {
        const { data } = await this.client.post(`${this.getSitePath()}/schedules`, params);
        return data;
    }
    async updateSchedule(id, params) {
        const { data } = await this.client.put(`${this.getSitePath()}/schedules/${id}`, params);
        return data;
    }
    async toggleSchedule(id, enabled) {
        const { data } = await this.client.patch(`${this.getSitePath()}/schedules/${id}/toggle`, { enabled });
        return data;
    }
    async deleteSchedule(id) {
        const { data } = await this.client.delete(`${this.getSitePath()}/schedules/${id}`);
        return data;
    }
    // ===========================================================================
    // Firmware / OTA
    // ===========================================================================
    async getFirmwareList(product) {
        const { data } = await this.client.get('/firmware', { params: product ? { product } : undefined });
        return data.firmware ?? data;
    }
    async getFirmware(id) {
        const { data } = await this.client.get(`/firmware/${id}`);
        return data;
    }
    async uploadFirmware(file, version, product, releaseNotes) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('version', version);
        formData.append('product', product);
        if (releaseNotes)
            formData.append('releaseNotes', releaseNotes);
        const { data } = await this.client.post('/firmware/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    }
    async deleteFirmware(id) {
        const { data } = await this.client.delete(`/firmware/${id}`);
        return data;
    }
    async deployFirmware(firmwareId, params) {
        const { data } = await this.client.post(`/firmware/${firmwareId}/deploy`, params);
        return data;
    }
    async getDeployments(filters) {
        const { data } = await this.client.get(`${this.getSitePath()}/firmware/deployments`, { params: filters });
        return data.deployments ?? data;
    }
    async cancelDeployment(deploymentId) {
        const { data } = await this.client.post(`/firmware/deployments/${deploymentId}/cancel`);
        return data;
    }
    async getVersionMatrix() {
        const { data } = await this.client.get(`${this.getSitePath()}/firmware/matrix`);
        return data.gateways ?? data;
    }
    // ===========================================================================
    // Virtual Devices (room emulator control plane) — /dev/virtual/*, not /api/
    //
    // Edge proxies these to the room emulator's own control API; see
    // Edge/src/routes/dev-virtual.ts and services/room-emulator/src/controlApi.ts.
    // The route surface is fixed on both sides (plan §2.4) — every call below
    // is a thin wrapper over one listed route, never a constructed path.
    // ===========================================================================
    async getVirtualRoutes() {
        const { data } = await this.devClient.get('/routes');
        return data;
    }
    async getVirtualHealth() {
        const { data } = await this.devClient.get('/health');
        return data;
    }
    async getVirtualProfiles() {
        const { data } = await this.devClient.get('/profiles');
        return data;
    }
    async getVirtualScenarios() {
        const { data } = await this.devClient.get('/scenarios');
        return data;
    }
    async getVirtualInstances() {
        const { data } = await this.devClient.get('/instances');
        return data.instances;
    }
    async getVirtualInstance(hwUid) {
        const { data } = await this.devClient.get(`/instances/${hwUid}`);
        return data;
    }
    async addVirtualInstance(params) {
        const { data } = await this.devClient.post('/instances', params);
        return data;
    }
    async batchAddVirtualInstances(params) {
        const { data } = await this.devClient.post('/instances/batch', params);
        return data;
    }
    async modifyVirtualInstance(hwUid, params) {
        const { data } = await this.devClient.patch(`/instances/${hwUid}`, params);
        return data;
    }
    async removeVirtualInstance(hwUid, force = false) {
        // `data: {}` for the same reason the bodyless POSTs above need it: this
        // instance always sends `Content-Type: application/json`, and Fastify's
        // JSON parser refuses that header on a truly empty body.
        const { data } = await this.devClient.delete(`/instances/${hwUid}`, {
            params: force ? { force: 'true' } : undefined,
            data: {},
        });
        return data;
    }
    async removeAllVirtualInstances(force = false) {
        const { data } = await this.devClient.delete('/instances', {
            params: force ? { force: 'true' } : undefined,
            data: {},
        });
        return data;
    }
    async startAllVirtualInstances(all = false) {
        const { data } = await this.devClient.post('/instances/start-all', { all });
        return data;
    }
    async stopAllVirtualInstances() {
        // The instance always sends `Content-Type: application/json` (axios
        // instance default), so a truly bodyless POST leaves an empty body
        // labeled as JSON — Fastify's strict JSON parser refuses that with
        // FST_ERR_CTP_EMPTY_JSON_BODY. Send `{}` explicitly on every no-payload
        // POST/PATCH through this client rather than omitting the body.
        const { data } = await this.devClient.post('/instances/stop-all', {});
        return data;
    }
    async startVirtualInstance(hwUid) {
        const { data } = await this.devClient.post(`/instances/${hwUid}/start`, {});
        return data;
    }
    async stopVirtualInstance(hwUid) {
        const { data } = await this.devClient.post(`/instances/${hwUid}/stop`, {});
        return data;
    }
    async restartVirtualInstance(hwUid) {
        const { data } = await this.devClient.post(`/instances/${hwUid}/restart`, {});
        return data;
    }
    /** Device-side channel write — a guest pressing a button, not an Edge command. */
    async inputVirtualInstance(hwUid, channel, values) {
        const { data } = await this.devClient.post(`/instances/${hwUid}/input`, { channel, values });
        return data;
    }
    /**
     * Answer Edge's proof-of-presence challenge in-process, so a hand-claimed
     * virtual unit can be unclaimed from outside the emulator (plan §2.4 seam;
     * carried findings, "the one route with no CLI counterpart"). Feed the
     * `presence` result to `unclaimGateway` via a presence-header encoder.
     */
    async virtualInstancePresenceAttestation(hwUid, challenge) {
        const { data } = await this.devClient.post(`/instances/${hwUid}/presence-attestation`, { challenge });
        return data;
    }
    async getVirtualFleet() {
        const { data } = await this.devClient.get('/fleet');
        return data;
    }
    async getVirtualFleetRoles() {
        const { data } = await this.devClient.get('/fleet/roles');
        return data;
    }
    async commissionVirtualFleet(params) {
        const { data } = await this.devClient.post('/fleet/commission', params);
        return data;
    }
    async decommissionVirtualFleet() {
        const { data } = await this.devClient.post('/fleet/decommission', {});
        return data;
    }
    async recoverVirtualFleet(params) {
        const { data } = await this.devClient.post('/fleet/recover', params);
        return data;
    }
    async driveVirtualFleetChannel(unit, channel, values) {
        const { data } = await this.devClient.post('/fleet/channel', { unit, channel, values });
        return data;
    }
    async driveVirtualFleetFlag(unit, flag, value) {
        const { data } = await this.devClient.post('/fleet/flag', { unit, flag, value });
        return data;
    }
    async measureVirtualFleetRate(durationMs) {
        const { data } = await this.devClient.post('/fleet/measure', durationMs ? { durationMs } : {});
        return data;
    }
    async cycleVirtualFleet(params) {
        const { data } = await this.devClient.post('/fleet/cycle', params);
        return data;
    }
}
const CLAIM_STATES = ['pending', 'confirmed', 'unsupported', 'error'];
/** One device row from either gateway response, in camelCase or snake_case. */
export function mapGatewayDevice(d) {
    // `null` is an answer (the relay has no claim ACK for it), so presence is tested, not truthiness.
    const pick = (camel, snake) => (camel in d ? d[camel] : d[snake]);
    const claim = pick('claimState', 'claim_state');
    const relay = pick('profileRelay', 'profile_relay');
    const device = {
        id: String(d.id ?? ''),
        hwUid: String(d.hwUid ?? d.hw_uid ?? ''),
        deviceCode: String(d.deviceCode ?? d.device_code ?? ''),
        displayName: (d.displayName ?? d.display_name ?? null),
        model: (d.model ?? null),
        firmwareVersion: (d.firmwareVersion ?? d.firmware_version ?? null),
        deviceType: String(d.deviceType ?? d.device_type ?? 'kanx'),
        pa: (d.pa ?? null),
        isSelf: Boolean(d.isSelf ?? d.is_self ?? false),
        status: String(d.status ?? 'offline'),
        lastSeenAt: (d.lastSeenAt ?? d.last_seen_at ?? null),
        channelCount: Number(d.channelCount ?? d.channel_count ?? 0),
    };
    if (Array.isArray(d.features))
        device.features = d.features.filter((f) => typeof f === 'string');
    const moved = pick('movedTo', 'moved_to');
    if (moved !== undefined)
        device.movedTo = typeof moved === 'string' ? moved : null;
    if (d.addressChanged !== undefined)
        device.addressChanged = d.addressChanged === true;
    if (claim !== undefined)
        device.claimState = CLAIM_STATES.includes(claim) ? claim : null;
    const rc = pick('claimRc', 'claim_rc');
    if (rc !== undefined)
        device.claimRc = typeof rc === 'number' ? rc : null;
    if (relay && typeof relay === 'object')
        device.profileRelay = { profiles: Array.isArray(relay.profiles) ? relay.profiles.filter((p) => typeof p === 'string') : [] };
    if (d.metadata !== undefined)
        device.metadata = (d.metadata ?? null);
    return device;
}
// =============================================================================
// Gateway Normalization
// =============================================================================
/** The five-figure heap health the API projects (`heapProjection`); anything else is "not reported". */
function normalizeHeap(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const h = raw;
    const figure = (v) => typeof v === 'number' && Number.isFinite(v);
    if (!figure(h.freeB) || !figure(h.minFreeB) || !figure(h.largestBlockB) || !figure(h.deferred) || typeof h.warn !== 'boolean')
        return null;
    return { freeB: h.freeB, minFreeB: h.minFreeB, largestBlockB: h.largestBlockB, deferred: h.deferred, warn: h.warn };
}
function normalizeGateway(raw) {
    return {
        heap: normalizeHeap(raw.heap),
        id: String(raw.id ?? ''),
        hwUid: String(raw.hwUid ?? raw.hw_uid ?? ''),
        gatewayCode: String(raw.gatewayCode ?? raw.gateway_code ?? ''),
        displayName: String(raw.displayName ?? raw.display_name ?? ''),
        model: String(raw.model ?? ''),
        firmwareVersion: String(raw.firmwareVersion ?? raw.firmware_version ?? ''),
        status: raw.status ?? 'offline',
        lastStatusAt: String(raw.lastStatusAt ?? raw.last_status_at ?? ''),
        identify: Boolean(raw.identify ?? false),
        appliedTemplateCode: (raw.appliedTemplateCode ?? raw.applied_template_code ?? null),
        appliedTemplateName: (raw.appliedTemplateName ?? raw.applied_template_name ?? null),
        templateOutdated: Boolean(raw.templateOutdated ?? raw.template_outdated ?? false),
        linkedRooms: (raw.linkedRooms ?? raw.linked_rooms ?? null),
    };
}
/**
 * `from`/`to` are the names every other range-taking route in this client uses,
 * but `GET /locks/events` and `GET /locks/:lockCode/events` read `startDate` and
 * `endDate`. Fastify's querystring schema there does not reject unknown members,
 * so a `from`/`to` pair was accepted, ignored, and silently returned the
 * unfiltered range. The translation happens once, here, rather than at each
 * caller — BMS-spec docs/edge/rest-api.md §Access Control.
 */
function lockEventQuery(filters) {
    if (!filters)
        return undefined;
    const { from, to, ...rest } = filters;
    return {
        ...rest,
        ...(from ? { startDate: from } : {}),
        ...(to ? { endDate: to } : {}),
    };
}
export const CHIP_TYPE_META = {
    mifare_ultralight_aes: { label: 'MIFARE Ultralight AES' },
    mifare_plus_ev2: { label: 'MIFARE Plus EV2' },
};
export const CHIP_TYPE_LABELS = Object.fromEntries(Object.entries(CHIP_TYPE_META).map(([chipType, meta]) => [chipType, meta.label]));
export async function getSigningKeys(siteCode) {
    const res = await fetch(`/api/sites/${siteCode}/access/keys`, { credentials: 'include' });
    if (!res.ok)
        throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
    return res.json();
}
/**
 * `flagsProtected` bits a role template writes onto the cards issued from it.
 * BMS-spec docs/access/cards.md §Protected flags. Bit 1 and bits 4-15 are
 * reserved.
 */
export const CARD_PROTECTED_FLAGS = {
    temporary: 1 << 0,
    /** The only category bypass in the system: guest-room locks, ACL or not. */
    housekeeping: 1 << 2,
    antiPassback: 1 << 3,
};
// ---------------------------------------------------------------------------
// Visitors
//
// A visitor is a person expected at the site for a bounded occasion. The
// credential they carry is an ordinary access card, issued through `issueCard`
// with `visitId` set: there is no visitor credential type, no visitor card
// endpoint and no visitor-specific expiry.
// BMS-spec docs/access/visitors.md.
// ---------------------------------------------------------------------------
/** Stored visit statuses. `no_show` and `overstayed` are never among them. */
export const VISIT_STATUSES = ['expected', 'checked_in', 'checked_out', 'cancelled'];
/**
 * The three permitted transitions, and all of them. `checked_out` and
 * `cancelled` are terminal, so a UI must not offer a control that leaves either.
 */
export const PERMITTED_VISIT_TRANSITIONS = {
    checked_in: ['expected'],
    checked_out: ['checked_in'],
    cancelled: ['expected'],
};
/** Published limits. BMS-spec docs/access/schedules.md §Limits. */
export const MAX_SCHEDULE_WINDOWS = 8;
export const MAX_LOCK_SCHEDULE_ATTACHMENTS = 4;
// =============================================================================
// Singleton API Client Instance
// =============================================================================
let apiClientInstance = null;
export function getApiClient(config) {
    if (!apiClientInstance || config) {
        apiClientInstance = new BmsApiClient(config);
    }
    return apiClientInstance;
}
export function resetApiClient() {
    apiClientInstance = null;
}
//# sourceMappingURL=client.js.map